import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
  Image,
  Trophy,
  Info,
  RefreshCw,
  Loader2,
  Tag,
  ExternalLink,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import PlatformBadge from "@/components/PlatformBadge";

const API = `${getApiUrl()}/api`;

export default function LibraryMaintenance() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [maintenanceData, setMaintenanceData] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  
  // Initialize expanded sections from URL params
  const [expandedSections, setExpandedSections] = useState(() => {
    const sections = searchParams.get("sections");
    if (sections) {
      try {
        return JSON.parse(decodeURIComponent(sections));
      } catch {
        return {
          discrepancies: true,
          incompleteSync: true,
          missingImages: true,
          missingAchievements: true,
          missingInfo: true,
          incompleteMetadata: true,
        };
      }
    }
    return {
      discrepancies: true,
      incompleteSync: true,
      missingImages: true,
      missingAchievements: true,
      missingInfo: true,
      incompleteMetadata: true,
    };
  });

  // Save expanded sections to URL when they change
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set("sections", encodeURIComponent(JSON.stringify(expandedSections)));
    setSearchParams(params, { replace: true });
  }, [expandedSections, searchParams, setSearchParams]);

  // Restore scroll position after data loads
  useEffect(() => {
    if (!loading && maintenanceData) {
      // Check sessionStorage for saved scroll position
      const scrollPos = sessionStorage.getItem('library_maintenance_scroll_position');
      
      if (scrollPos) {
        // Clear session storage after using
        sessionStorage.removeItem('library_maintenance_scroll_position');
        // Use setTimeout to ensure DOM has updated with content
        setTimeout(() => {
          window.scrollTo(0, parseInt(scrollPos) || 0);
        }, 50);
      }
    }
  }, [loading, maintenanceData]);

  // Save scroll position before navigating away (using mousedown to capture before navigation)
  useEffect(() => {
    const handleMouseDown = (e) => {
      const link = e.target.closest('a');
      if (link && link.href && link.href.includes('/games/')) {
        // Only save when navigating to a game detail page
        if (link.href.match(/\/games\/[a-zA-Z0-9-]+$/)) {
          sessionStorage.setItem('library_maintenance_scroll_position', window.scrollY.toString());
        }
      }
    };
    
    document.addEventListener('mousedown', handleMouseDown, true);
    return () => document.removeEventListener('mousedown', handleMouseDown, true);
  }, []);

  useEffect(() => {
    fetchMaintenanceData();
    fetchPlatforms();
  }, []);

  const fetchPlatforms = async () => {
    try {
      const response = await axios.get(`${API}/platforms`);
      setPlatforms(response.data);
    } catch (error) {
      console.error("Failed to fetch platforms:", error);
    }
  };

  const fetchMaintenanceData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/library/maintenance`);
      setMaintenanceData(response.data);
    } catch (error) {
      console.error("Failed to fetch maintenance data:", error);
      toast.error("Failed to load maintenance data");
    } finally {
      setLoading(false);
    }
  };

  const getPlatformColor = (platformId) => {
    const platform = platforms.find((p) => p.id === platformId);
    return platform?.color || "#666";
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white/50" />
      </div>
    );
  }

  const {
    discrepancies = [],
    incompleteSync = [],
    missingImages = [],
    missingAchievements = [],
    missingInfo = [],
    incompleteMetadata = [],
    summary = {},
  } = maintenanceData || {};

  const totalIssues = incompleteSync.length + missingImages.length + missingAchievements.length + missingInfo.length + incompleteMetadata.length;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/settings">
            <Button variant="ghost" size="icon" className="text-white/50 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl text-white">Library Maintenance</h1>
            <p className="text-white/50 text-sm">Review and fix issues in your game library</p>
          </div>
        </div>

        {/* Summary Card */}
        <Card className="bg-[#0A0A0A] border-white/10 mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-white">{summary.totalGames || 0}</p>
                <p className="text-white/50 text-xs">Total Games</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-400">{summary.healthyGames || 0}</p>
                <p className="text-white/50 text-xs">Healthy</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-400">{totalIssues}</p>
                <p className="text-white/50 text-xs">Issues Found</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">
                  {summary.totalGames > 0 ? Math.round((summary.healthyGames / summary.totalGames) * 100) : 0}%
                </p>
                <p className="text-white/50 text-xs">Health Score</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Discrepancies */}
        {discrepancies.length > 0 && (
          <Card className="bg-[#0A0A0A] border-red-500/30 mb-6">
            <CardHeader
              className="cursor-pointer"
              onClick={() => toggleSection("discrepancies")}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  <CardTitle className="text-lg text-red-400">Profile Score Discrepancies</CardTitle>
                </div>
                {expandedSections.discrepancies ? (
                  <ChevronUp className="w-5 h-5 text-white/50" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-white/50" />
                )}
              </div>
              <CardDescription className="text-white/50">
                Differences between your connected profiles and library data
              </CardDescription>
            </CardHeader>
            {expandedSections.discrepancies && (
              <CardContent className="space-y-4">
                {discrepancies.map((disc, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-red-500/5 border border-red-500/20 rounded-lg"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div
                        className="w-3 h-3 rounded-sm"
                        style={{ backgroundColor: getPlatformColor(disc.platform) }}
                      />
                      <span className="font-medium text-white">{disc.platformName}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-white/50">Profile Score</p>
                        <p className="text-white font-medium">{disc.profileScore?.toLocaleString() || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-white/50">Library Score</p>
                        <p className="text-white font-medium">{disc.libraryScore?.toLocaleString() || 0}</p>
                      </div>
                      <div>
                        <p className="text-white/50">Profile Achievements</p>
                        <p className="text-white font-medium">{disc.profileAchievements?.toLocaleString() || "N/A"}</p>
                      </div>
                      <div>
                        <p className="text-white/50">Library Achievements</p>
                        <p className="text-white font-medium">{disc.libraryAchievements?.toLocaleString() || 0}</p>
                      </div>
                    </div>
                    {(disc.scoreDifference > 0 || disc.achievementDifference > 0) && (
                      <div className="mt-3 p-2 bg-red-500/10 rounded text-xs text-red-300">
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        Missing: {disc.scoreDifference > 0 && `${disc.scoreDifference.toLocaleString()} score`}
                        {disc.scoreDifference > 0 && disc.achievementDifference > 0 && " / "}
                        {disc.achievementDifference > 0 && `${disc.achievementDifference.toLocaleString()} achievements`}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        )}

        {/* Incomplete Sync Games */}
        {incompleteSync.length > 0 && (
          <Card className="bg-[#0A0A0A] border-amber-500/30 mb-6">
            <CardHeader
              className="cursor-pointer"
              onClick={() => toggleSection("incompleteSync")}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-amber-400" />
                  <CardTitle className="text-lg text-amber-400">
                    Incomplete Sync ({incompleteSync.length})
                  </CardTitle>
                </div>
                {expandedSections.incompleteSync ? (
                  <ChevronUp className="w-5 h-5 text-white/50" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-white/50" />
                )}
              </div>
              <CardDescription className="text-white/50">
                Games with achievement counts that don&apos;t match the platform API
              </CardDescription>
            </CardHeader>
            {expandedSections.incompleteSync && (
              <CardContent>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {incompleteSync.map((game) => (
                    <Link
                      key={game.id}
                      to={`/games/${game.id}`}
                      className="flex items-center justify-between p-3 bg-black/30 rounded-lg hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-2 h-2 rounded-sm"
                          style={{ backgroundColor: getPlatformColor(game.platform) }}
                        />
                        <span className="text-white text-sm">{game.name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-white/50">
                        <span>{game.localCount || 0}/{game.apiCount || "?"} achievements</span>
                        <ExternalLink className="w-3 h-3" />
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Missing Images */}
        {missingImages.length > 0 && (
          <Card className="bg-[#0A0A0A] border-white/10 mb-6">
            <CardHeader
              className="cursor-pointer"
              onClick={() => toggleSection("missingImages")}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Image className="w-5 h-5 text-blue-400" />
                  <CardTitle className="text-lg text-blue-400">
                    Missing Box Art ({missingImages.length})
                  </CardTitle>
                </div>
                {expandedSections.missingImages ? (
                  <ChevronUp className="w-5 h-5 text-white/50" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-white/50" />
                )}
              </div>
              <CardDescription className="text-white/50">
                Games without box art images
              </CardDescription>
            </CardHeader>
            {expandedSections.missingImages && (
              <CardContent>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {missingImages.map((game) => (
                    <Link
                      key={game.id}
                      to={`/games/${game.id}`}
                      className="flex items-center justify-between p-3 bg-black/30 rounded-lg hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-2 h-2 rounded-sm"
                          style={{ backgroundColor: getPlatformColor(game.platform) }}
                        />
                        <span className="text-white text-sm">{game.name}</span>
                      </div>
                      <ExternalLink className="w-3 h-3 text-white/50" />
                    </Link>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Missing Achievements */}
        {missingAchievements.length > 0 && (
          <Card className="bg-[#0A0A0A] border-white/10 mb-6">
            <CardHeader
              className="cursor-pointer"
              onClick={() => toggleSection("missingAchievements")}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-purple-400" />
                  <CardTitle className="text-lg text-purple-400">
                    No Achievements ({missingAchievements.length})
                  </CardTitle>
                </div>
                {expandedSections.missingAchievements ? (
                  <ChevronUp className="w-5 h-5 text-white/50" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-white/50" />
                )}
              </div>
              <CardDescription className="text-white/50">
                Games with no achievements added
              </CardDescription>
            </CardHeader>
            {expandedSections.missingAchievements && (
              <CardContent>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {missingAchievements.map((game) => (
                    <Link
                      key={game.id}
                      to={`/games/${game.id}`}
                      className="flex items-center justify-between p-3 bg-black/30 rounded-lg hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-2 h-2 rounded-sm"
                          style={{ backgroundColor: getPlatformColor(game.platform) }}
                        />
                        <span className="text-white text-sm">{game.name}</span>
                      </div>
                      <ExternalLink className="w-3 h-3 text-white/50" />
                    </Link>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Missing Info */}
        {missingInfo.length > 0 && (
          <Card className="bg-[#0A0A0A] border-white/10 mb-6">
            <CardHeader
              className="cursor-pointer"
              onClick={() => toggleSection("missingInfo")}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-gray-400" />
                  <CardTitle className="text-lg text-gray-400">
                    Missing Information ({missingInfo.length})
                  </CardTitle>
                </div>
                {expandedSections.missingInfo ? (
                  <ChevronUp className="w-5 h-5 text-white/50" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-white/50" />
                )}
              </div>
              <CardDescription className="text-white/50">
                Games missing publisher, developer, or release date
              </CardDescription>
            </CardHeader>
            {expandedSections.missingInfo && (
              <CardContent>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {missingInfo.slice(0, 100).map((game) => (
                    <Link
                      key={game.id}
                      to={`/games/${game.id}`}
                      className="flex items-center justify-between p-3 bg-black/30 rounded-lg hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-2 h-2 rounded-sm"
                          style={{ backgroundColor: getPlatformColor(game.platform) }}
                        />
                        <span className="text-white text-sm">{game.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-white/40 text-xs">
                          {game.missingFields?.join(", ")}
                        </span>
                        <ExternalLink className="w-3 h-3 text-white/50" />
                      </div>
                    </Link>
                  ))}
                  {missingInfo.length > 100 && (
                    <p className="text-white/40 text-center text-sm py-2">
                      And {missingInfo.length - 100} more...
                    </p>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Incomplete Metadata (failed IGDB enrichment) */}
        {incompleteMetadata.length > 0 && (
          <Card className="bg-[#0A0A0A] border-orange-500/30">
            <CardHeader 
              className="cursor-pointer hover:bg-white/5 transition-colors rounded-t-lg"
              onClick={() => toggleSection("incompleteMetadata")}
            >
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Tag className="w-5 h-5 text-orange-400" />
                  Incomplete Metadata ({incompleteMetadata.length})
                </CardTitle>
                {expandedSections.incompleteMetadata ? (
                  <ChevronUp className="w-5 h-5 text-white/50" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-white/50" />
                )}
              </div>
              <CardDescription className="text-white/50 text-sm">
                Games missing publisher, developer, release date, or genres. Click to expand.
              </CardDescription>
            </CardHeader>
            {expandedSections.incompleteMetadata && (
              <CardContent>
                <div className="space-y-2">
                  {incompleteMetadata.slice(0, 100).map((game) => (
                    <Link
                      key={game.id}
                      to={`/games/${game.id}`}
                      className="flex items-center justify-between p-3 bg-[#121212] rounded-lg border border-white/5 hover:border-orange-500/30 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Tag className="w-4 h-4 text-orange-400" />
                        <span className="text-white">{game.name}</span>
                        <span className="text-xs text-white/40">{game.platform}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-white/40 text-xs">
                          Missing: {game.missingFields?.join(", ") || "metadata"}
                        </span>
                        <ExternalLink className="w-3 h-3 text-white/50" />
                      </div>
                    </Link>
                  ))}
                  {incompleteMetadata.length > 100 && (
                    <p className="text-white/40 text-center text-sm py-2">
                      And {incompleteMetadata.length - 100} more...
                    </p>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* All Clear */}
        {totalIssues === 0 && discrepancies.length === 0 && (
          <Card className="bg-[#0A0A0A] border-green-500/30">
            <CardContent className="pt-6 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">Library is Healthy!</h3>
              <p className="text-white/50 text-sm">
                All games have complete information and no sync issues detected.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Refresh Button */}
        <div className="mt-8 text-center">
          <Button
            onClick={fetchMaintenanceData}
            variant="outline"
            className="border-white/10 text-white/70 hover:bg-white/10"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Maintenance Data
          </Button>
        </div>
      </div>
    </div>
  );
}
